import "zod/compile";
import {
  componentDatasetKey,
  componentTextDatasetKey,
  ReportWidgetSchema,
  type ReportDataSource,
} from "@erp/shared-types";
import {
  parseCashQuery,
  type CashQueryErrorCode,
} from "./ar-cash-dashboard.grammar";

/**
 * The cash query catalog as a report-canvas data source. A Cash Dashboard
 * widget is a generic canvas widget (KPI, Table, Text, custom chart) whose
 * `source` names this catalog and holds a cash query; the page runs every
 * widget's query in one batched `arCashDashboard.query` request and hands
 * each answer to its widget as a dataset.
 *
 * A source's range pin is `source.range`, never the query's own `range`:
 * the page range and a pin are the canvas's concepts, so one place holds
 * them.
 */

export const CASH_QUERY_CATALOG = "ar-cash";

/** A problem with a source, at a path within it (e.g. ["query", "measures", 0]). */
export interface SourceIssue {
  /** The query error code it would get from the endpoint. */
  code: CashQueryErrorCode;
  path: (string | number)[];
  message: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The cash query a source runs: its query, pinned to the source's range. */
export function cashSourceQuery(source: ReportDataSource): unknown {
  if (!source.range || !isRecord(source.query)) return source.query;
  return { ...source.query, range: source.range };
}

/**
 * Why a source is not a cash query the endpoint answers, by path within the
 * source; empty when it is. `totals` asks for one row of totals (a text
 * source: an aggregate with no dimensions).
 */
export function cashSourceIssues(
  source: ReportDataSource,
  options: { totals?: boolean } = {},
): SourceIssue[] {
  if (source.catalog !== CASH_QUERY_CATALOG)
    return [
      {
        code: "not_in_catalog",
        path: ["catalog"],
        message: `Use the "${CASH_QUERY_CATALOG}" catalog`,
      },
    ];
  if (isRecord(source.query) && "range" in source.query)
    return [
      {
        code: "invalid_query",
        path: ["query", "range"],
        message:
          'Pin a range on the source ("range" beside "query"), not in the query',
      },
    ];
  const parsed = parseCashQuery(cashSourceQuery(source));
  if (!parsed.ok) {
    const at = parsed.error.path ?? [];
    return [
      {
        code: parsed.error.code,
        // The range a source sends is its own pin.
        path: at[0] === "range" ? at : ["query", ...at],
        message: parsed.error.message,
      },
    ];
  }
  if (
    options.totals &&
    (parsed.query.kind !== "aggregate" || parsed.query.dimensions.length > 0)
  )
    return [
      {
        code: "unsupported_combination",
        path: ["query"],
        message: "Use a query with no dimensions (one row of totals)",
      },
    ];
  return [];
}

/**
 * A Cash Dashboard widget: a generic canvas widget whose sources, if any,
 * are cash queries the endpoint answers. What the JSON editor and Nami's
 * layout tools validate a widget against.
 */
export const CashDashboardWidgetSchema = ReportWidgetSchema.superRefine(
  (widget, ctx) => {
    const sources = [
      ["source", widget.source, {}],
      ["textSource", widget.textSource, { totals: true }],
    ] as const;
    for (const [field, source, options] of sources) {
      if (!source) continue;
      for (const issue of cashSourceIssues(source, options))
        ctx.addIssue({
          code: "custom",
          message: issue.message,
          path: [field, ...issue.path],
        });
    }
  },
);

/** One keyed query of the page's batch, as written (the server parses it). */
export interface CashSourceQuery {
  key: string;
  query: unknown;
}

interface SourcedComponent {
  id: string;
  datasetKey?: string;
  source?: ReportDataSource;
  textSource?: ReportDataSource;
}

/** A cash source of a component, under the dataset key its answer is published as. */
export interface CashSourceEntry {
  key: string;
  source: ReportDataSource;
  /** A text source: one row of totals. */
  totals: boolean;
}

/**
 * A component's cash sources, keyed by the dataset each answer is published
 * as: its source, and its text source (pinned to the source's range unless
 * it has its own). Sources in other catalogs are left out.
 */
export function cashSourceEntries(
  component: SourcedComponent,
): CashSourceEntry[] {
  const entries: CashSourceEntry[] = [];
  const { source, textSource } = component;
  const key = componentDatasetKey(component);
  if (source?.catalog === CASH_QUERY_CATALOG && key)
    entries.push({ key, source, totals: false });
  const textKey = componentTextDatasetKey(component);
  if (textSource?.catalog === CASH_QUERY_CATALOG && textKey) {
    const range = textSource.range ?? source?.range;
    entries.push({
      key: textKey,
      source: range ? { ...textSource, range } : textSource,
      totals: true,
    });
  }
  return entries;
}

/**
 * Every component's cash sources, once per dataset key (components sharing
 * a dataset share its source; the first one's wins).
 */
export function cashSourceEntriesOf(
  components: readonly SourcedComponent[],
): CashSourceEntry[] {
  const seen = new Set<string>();
  return components.flatMap(cashSourceEntries).filter((entry) => {
    if (seen.has(entry.key)) return false;
    seen.add(entry.key);
    return true;
  });
}

/** Every component's cash queries, keyed by dataset, as the page batches them. */
export function cashBatchQueries(
  components: readonly SourcedComponent[],
): CashSourceQuery[] {
  return cashSourceEntriesOf(components).map((entry) => ({
    key: entry.key,
    query: cashSourceQuery(entry.source),
  }));
}
